import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils/cn";
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { cva } from "class-variance-authority";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import * as React from "react";

// ================================
// Size — `size` on the root cascades to the popover parts via context so
// spacing/font track the field without per-call overrides. Extend the scale
// by adding a key to each variant map (à la buttonVariants).
// ================================

type ComboboxSize = "default" | "sm" | "xs";
const ComboboxSizeContext = React.createContext<ComboboxSize>("default");
const useComboboxSize = (): ComboboxSize => React.useContext(ComboboxSizeContext);

/** Height of the field shell (the InputGroup). */
export const comboboxFieldVariants = cva("", {
  variants: { size: { default: "h-9", sm: "h-8", xs: "h-6" } },
  defaultVariants: { size: "default" },
});

const comboboxItemVariants = cva(
  "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 outline-hidden select-none data-[disabled]:pointer-events-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      size: { default: "py-1.5 text-sm", sm: "py-1 text-xs", xs: "py-0.5 text-xs" },
    },
    defaultVariants: { size: "default" },
  },
);

// Mirrors the item padding/text so the empty message reads like a single
// (muted, non-interactive) entry.
const comboboxEmptyVariants = cva("px-2 text-muted-foreground empty:hidden", {
  variants: { size: { default: "py-1.5 text-sm", sm: "py-1 text-xs", xs: "py-0.5 text-xs" } },
  defaultVariants: { size: "default" },
});

/** Text size for the field input, chips, and chips-input. */
const comboboxTextVariants = cva("", {
  variants: { size: { default: "text-sm", sm: "text-xs md:text-xs", xs: "text-xs md:text-xs" } },
  defaultVariants: { size: "default" },
});

/** Trigger / clear icon size. */
const comboboxIconVariants = cva("shrink-0", {
  variants: { size: { default: "size-4", sm: "size-3.5", xs: "size-3.5" } },
  defaultVariants: { size: "default" },
});

// ================================
// Root
// ================================

type ComboboxRootProps<
  Value,
  Multiple extends boolean | undefined = false,
> =
  & BaseCombobox.Root.Props<Value, Multiple>
  & { size?: ComboboxSize; };

const ComboboxRoot = <Value, Multiple extends boolean | undefined = false>(
  { size = "default", ...props }: ComboboxRootProps<Value, Multiple>,
): React.JSX.Element => {
  return (
    <ComboboxSizeContext.Provider value={size}>
      <BaseCombobox.Root data-slot="combobox" {...props} />
    </ComboboxSizeContext.Provider>
  );
};

// ================================
// Value
// ================================

const ComboboxValue = BaseCombobox.Value;

// ================================
// Trigger — the chevron toggle (rendered as the InputGroup's trailing button)
// ================================

type ComboboxTriggerProps = React.ComponentProps<typeof BaseCombobox.Trigger>;

const ComboboxTrigger: React.FC<ComboboxTriggerProps> = ({ className, children, ...props }) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Trigger
      data-slot="combobox-trigger"
      className={cn("text-muted-foreground", className)}
      {...props}
    >
      {children ?? <ChevronDownIcon className={comboboxIconVariants({ size })} />}
    </BaseCombobox.Trigger>
  );
};

// ================================
// Clear — the clear (x) button; Base UI shows it only when there's a value
// ================================

type ComboboxClearProps = React.ComponentProps<typeof BaseCombobox.Clear>;

const ComboboxClear: React.FC<ComboboxClearProps> = ({ className, children, ...props }) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="ghost" size="icon-xs" />}
      className={cn("text-muted-foreground", className)}
      {...props}
    >
      {children ?? <XIcon className={comboboxIconVariants({ size })} />}
    </BaseCombobox.Clear>
  );
};

// ================================
// Input — the field (a real input). showTrigger = chevron, showClear = clear x.
// The trigger hides itself when the clear button is present (one trailing slot).
// ================================

type ComboboxInputProps =
  & React.ComponentProps<typeof BaseCombobox.Input>
  & { showTrigger?: boolean; showClear?: boolean; disabled?: boolean; };

const ComboboxInput = React.forwardRef<HTMLDivElement, ComboboxInputProps>(({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}, ref) => {
  const size = useComboboxSize();
  return (
    <InputGroup ref={ref} size={size} className={cn(comboboxFieldVariants({ size }), className)}>
      <BaseCombobox.Input
        render={<InputGroupInput disabled={disabled} />}
        className={comboboxTextVariants({ size })}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <ComboboxTrigger
            render={<InputGroupButton size="icon-xs" variant="ghost" disabled={disabled} />}
            className="group-has-data-[slot=combobox-clear]/input-group:hidden"
          />
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
});
ComboboxInput.displayName = "ComboboxInput";

// ================================
// Content — Portal + Positioner + Popup composite
// ================================

type ComboboxContentProps =
  & React.ComponentProps<typeof BaseCombobox.Popup>
  & Pick<
    React.ComponentProps<typeof BaseCombobox.Positioner>,
    "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
  >
  & {
    /** "match" (default) = trigger width. "auto" = grow to fit items (capped at viewport). */
    width?: "match" | "auto";
    /** Optional portal target override. Defaults to the document body. */
    container?: React.ComponentProps<typeof BaseCombobox.Portal>["container"];
  };

const ComboboxContent: React.FC<ComboboxContentProps> = ({
  className,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset = 0,
  anchor,
  width = "match",
  container,
  ...props
}) => {
  return (
    <BaseCombobox.Portal {...(container != null && { container })}>
      <BaseCombobox.Positioner
        data-slot="combobox-positioner"
        className="z-50 outline-hidden"
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        {...(anchor !== undefined && { anchor })}
      >
        <BaseCombobox.Popup
          data-slot="combobox-content"
          className={cn(
            "max-w-[var(--available-width)] origin-[var(--transform-origin)] rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden transition-[transform,scale,opacity] duration-100 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
            width === "auto"
              ? "w-fit min-w-[var(--anchor-width)]"
              : "w-[var(--anchor-width)]",
            className,
          )}
          {...props}
        />
      </BaseCombobox.Positioner>
    </BaseCombobox.Portal>
  );
};

// ================================
// List
// ================================

type ComboboxListProps = React.ComponentProps<typeof BaseCombobox.List>;

const ComboboxList: React.FC<ComboboxListProps> = ({ className, ...props }) => {
  return (
    <BaseCombobox.List
      data-slot="combobox-list"
      className={cn(
        "max-h-[min(var(--available-height),20rem)] overflow-y-auto overscroll-contain outline-hidden",
        className,
      )}
      {...props}
    />
  );
};

// ================================
// Item (+ ItemIndicator baked in)
// ================================

type ComboboxItemProps = React.ComponentProps<typeof BaseCombobox.Item>;

const ComboboxItem: React.FC<ComboboxItemProps> = ({ className, children, ...props }) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Item
      data-slot="combobox-item"
      className={cn(comboboxItemVariants({ size }), className)}
      {...props}
    >
      {children}
      <span className="ml-auto flex size-4 shrink-0 items-center justify-center text-muted-foreground">
        <BaseCombobox.ItemIndicator data-slot="combobox-item-indicator">
          <CheckIcon className="size-4" />
        </BaseCombobox.ItemIndicator>
      </span>
    </BaseCombobox.Item>
  );
};

// ================================
// Group / Label / Collection / Empty / Separator
// ================================

const ComboboxGroup: React.FC<React.ComponentProps<typeof BaseCombobox.Group>> = (
  { className, ...props },
) => {
  return (
    <BaseCombobox.Group
      data-slot="combobox-group"
      className={cn("overflow-hidden p-1", className)}
      {...props}
    />
  );
};

const ComboboxLabel: React.FC<React.ComponentProps<typeof BaseCombobox.GroupLabel>> = (
  { className, ...props },
) => {
  return (
    <BaseCombobox.GroupLabel
      data-slot="combobox-label"
      className={cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className)}
      {...props}
    />
  );
};

const ComboboxCollection = BaseCombobox.Collection;

const ComboboxEmpty: React.FC<React.ComponentProps<typeof BaseCombobox.Empty>> = (
  { className, children, ...props },
) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Empty
      data-slot="combobox-empty"
      className={cn(comboboxEmptyVariants({ size }), className)}
      {...props}
    >
      {children ?? "No results found."}
    </BaseCombobox.Empty>
  );
};

const ComboboxSeparator: React.FC<React.ComponentProps<typeof BaseCombobox.Separator>> = (
  { className, ...props },
) => {
  return (
    <BaseCombobox.Separator
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
};

// ================================
// Chips (multi-select field) / Chip / ChipsInput
// ================================

const ComboboxChips = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseCombobox.Chips>
>(({ className, ...props }, ref) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Chips
      ref={ref}
      data-slot="combobox-chips"
      className={cn(
        "flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 shadow-xs focus-ring-within",
        comboboxFieldVariants({ size }),
        "h-auto",
        className,
      )}
      {...props}
    />
  );
});
ComboboxChips.displayName = "ComboboxChips";

type ComboboxChipProps = React.ComponentProps<typeof BaseCombobox.Chip> & {
  showRemove?: boolean;
};

const ComboboxChip: React.FC<ComboboxChipProps> = ({
  className,
  children,
  showRemove = true,
  ...props
}) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Chip
      data-slot="combobox-chip"
      className={cn(
        "flex items-center gap-1 rounded bg-secondary py-0.5 pr-1 pl-2 text-secondary-foreground has-disabled:pointer-events-none has-disabled:opacity-50",
        comboboxTextVariants({ size }),
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <BaseCombobox.ChipRemove
          data-slot="combobox-chip-remove"
          className="flex size-4 cursor-pointer items-center justify-center rounded-sm text-current hover:bg-secondary-foreground/20"
        >
          <XIcon className="size-3" />
        </BaseCombobox.ChipRemove>
      )}
    </BaseCombobox.Chip>
  );
};

const ComboboxChipsInput: React.FC<React.ComponentProps<typeof BaseCombobox.Input>> = (
  { className, ...props },
) => {
  const size = useComboboxSize();
  return (
    <BaseCombobox.Input
      data-slot="combobox-chips-input"
      className={cn(
        "min-w-16 flex-1 bg-transparent outline-none placeholder:text-placeholder",
        comboboxTextVariants({ size }),
        className,
      )}
      {...props}
    />
  );
};

// ================================
// Anchor helper (for anchoring the popup to a chips field)
// ================================

export function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

export const Combobox = Object.assign(ComboboxRoot, {
  Value: ComboboxValue,
  Trigger: ComboboxTrigger,
  Clear: ComboboxClear,
  Input: ComboboxInput,
  Content: ComboboxContent,
  List: ComboboxList,
  Item: ComboboxItem,
  Group: ComboboxGroup,
  Label: ComboboxLabel,
  Collection: ComboboxCollection,
  Empty: ComboboxEmpty,
  Separator: ComboboxSeparator,
  Chips: ComboboxChips,
  Chip: ComboboxChip,
  ChipsInput: ComboboxChipsInput,
  useFilter: BaseCombobox.useFilter,
});
